import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = {
  /** Optional label shown above the error message; useful when several boundaries coexist. */
  label?: string
  children: ReactNode
  /** Optional override render. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

type State = {
  error: Error | null
}

/**
 * Catches render-time errors inside its subtree and surfaces them in-place
 * rather than letting the whole React tree unmount (which is what produced the
 * blank-panel symptom in production builds).
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ''}]`, error, info)
  }

  reset = () => this.setState({ error: null })

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div
        role="alert"
        style={{
          padding: 12,
          margin: 12,
          border: '0.5px solid var(--crit-bd, #c33)',
          background: 'var(--crit-bg, rgba(204, 51, 51, 0.08))',
          color: 'var(--t-0)',
          borderRadius: 'var(--radius, 4px)',
          fontFamily: 'inherit',
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {this.props.label ?? 'Something went wrong'}
        </div>
        <div style={{ color: 'var(--crit, #c33)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>
          {error.message}
        </div>
        {error.stack && (
          <pre
            style={{
              fontSize: 10,
              color: 'var(--t-2)',
              background: 'var(--bg-2, rgba(0,0,0,0.15))',
              padding: 8,
              borderRadius: 'var(--radius, 4px)',
              maxHeight: 200,
              overflow: 'auto',
              margin: 0,
            }}
          >
            {error.stack}
          </pre>
        )}
        <button
          type="button"
          onClick={this.reset}
          style={{
            marginTop: 8,
            padding: '4px 10px',
            fontSize: 11,
            border: '0.5px solid var(--bd-1)',
            background: 'transparent',
            color: 'var(--t-0)',
            cursor: 'pointer',
            borderRadius: 'var(--radius, 4px)',
          }}
        >
          Retry
        </button>
      </div>
    )
  }
}
